import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import styles from './PreferredContract.module.scss'
import BackButton from '../../components/buttons/BackButton/BackButton'
import ToggleButtonGroupController from '../../components/ToggleButtonGroup/ToggleButtonGroupController'
import SimpleInputController from '../../components/simpleInput/SimpleInputController'
import IncentiveModal from '../onboarding/IncentiveModal'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { Tooltip } from 'react-tooltip'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { usePreferredContract } from '../../hooks'
import { useQueryClient } from '@tanstack/react-query'
import { updatePreferredContractInfo } from '../../services/api'
import { useToken } from '../../utils'

export const PreferredContract = () => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [activeRegion, setActiveRegion] = useState('local') // 'local' or 'global'
	const [isIncentiveModalOpen, setIsIncentiveModalOpen] = useState(false)
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const dropdownRef = React.useRef(null)

	// 선호 계약 정보 조회
	const { data: preferredContract, isLoading } = usePreferredContract()

	console.log('preferredContract', preferredContract)
	const token = useToken()

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		trigger,
		getValues,
		formState: { errors, isValid },
	} = useForm({
		mode: 'onChange',
		defaultValues: {
			local: {
				applyTax: 'before',
				payPeriod: 'week',
				cutPass: '',
				cutOut: '',
				incentiveOption: '',
				incentiveSettings: [],
				sponsorCap: true,
				jejudo: true,
				jejudoMoney: '',
			},
			global: {
				applyTax: 'before',
				payPeriod: 'week',
				cutPass: '',
				cutOut: '',
				incentiveOption: '',
				incentiveSettings: [],
				sponsorCap: true,
				overseasSupport: null,
			},
		},
	})

	const localData = watch('local')
	const globalData = watch('global')
	const activeData = activeRegion === 'local' ? localData : globalData

	const jejudo = watch('local.jejudo')
	const localIncentiveOption = watch('local.incentiveOption')
	const globalIncentiveOption = watch('global.incentiveOption')

	const [isLocalValid, setIsLocalValid] = useState(false)
	const [isGlobalValid, setIsGlobalValid] = useState(false)

	useEffect(() => {
		if (activeRegion === 'local') {
			setIsLocalValid(isValid)
		}
	}, [isValid, activeRegion])

	useEffect(() => {
		if (activeRegion === 'global') {
			setIsGlobalValid(isValid)
		}
	}, [isValid, activeRegion])

	useEffect(() => {
		if (activeRegion === 'local') {
			trigger('local')
		} else if (activeRegion === 'global') {
			trigger('global')
		}
	}, [activeRegion, trigger])

	// 서버에서 받아온 데이터를 폼에 채우기
	useEffect(() => {
		if (preferredContract && !isLoading) {
			const { domesticContract, overseasContract } = preferredContract

			// 국내 계약 정보
			if (domesticContract) {
				const localIncentives =
					domesticContract.incentives?.map((inc, index) => ({
						id: index + 1,
						start: inc.startRank,
						end: inc.endRank === 0 ? 'CUT' : inc.endRank,
						percentage: inc.percentage,
					})) || []

				setValue(
					'local.applyTax',
					domesticContract.isSalaryTaxIncluded ? 'after' : 'before',
				)
				setValue(
					'local.payPeriod',
					domesticContract.payrollCycles === 'weekly' ? 'week' : 'single',
				)
				setValue(
					'local.cutPass',
					formatNumberWithCommas(domesticContract.weeklySalaryCutThrough),
				)
				setValue(
					'local.cutOut',
					formatNumberWithCommas(domesticContract.weeklySalaryCutFail),
				)
				setValue(
					'local.incentiveOption',
					domesticContract.prizeType === 'displayed' ? 'amountDisplayed' : 'actualAmount',
				)
				setValue('local.incentiveSettings', localIncentives)
				setValue('local.jejudo', domesticContract.jejuTournamentParticipation || false)
				setValue(
					'local.jejudoMoney',
					formatNumberWithCommas(domesticContract.expenseReimbursementJeju),
				)
				setValue('local.sponsorCap', domesticContract.wearSponsoredHat || false)
			}

			// 해외 계약 정보
			if (overseasContract) {
				const globalIncentives =
					overseasContract.incentives?.map((inc, index) => ({
						id: index + 1,
						start: inc.startRank,
						end: inc.endRank === 0 ? 'CUT' : inc.endRank,
						percentage: inc.percentage,
					})) || []

				setValue(
					'global.applyTax',
					overseasContract.isSalaryTaxIncluded ? 'after' : 'before',
				)
				setValue(
					'global.payPeriod',
					overseasContract.payrollCycles === 'weekly' ? 'week' : 'single',
				)
				setValue(
					'global.cutPass',
					formatNumberWithCommas(overseasContract.weeklySalaryCutThrough),
				)
				setValue(
					'global.cutOut',
					formatNumberWithCommas(overseasContract.weeklySalaryCutFail),
				)
				setValue(
					'global.incentiveOption',
					overseasContract.prizeType === 'displayed' ? 'amountDisplayed' : 'actualAmount',
				)
				setValue('global.incentiveSettings', globalIncentives)
				setValue(
					'global.overseasSupport',
					Number(overseasContract.expenseReimbursement) || 0,
				)
				setValue('global.sponsorCap', overseasContract.wearSponsoredHat || false)
			}
		}
	}, [preferredContract, isLoading, setValue])

	const applyTaxOption = [
		{ label: '세전', value: 'before' },
		{ label: '세후', value: 'after' },
	]

	const payPeriodOption = [
		{ label: '주급', value: 'week' },
		{ label: '일시불제(기간계약)', value: 'single' },
	]

	const jejudoOption = [
		{ label: '지원', value: true },
		{ label: '미지원', value: false },
	]

	const sponsorCapOption = [
		{ label: '착용', value: true },
		{ label: '미착용', value: false },
	]

	const overseasSupportOptions = [
		{ label: '미지원', value: 0 },
		{ label: '10%', value: 10 },
		{ label: '30%', value: 30 },
		{ label: '50%', value: 50 },
		{ label: '70%', value: 70 },
		{ label: '90%', value: 90 },
		{ label: '100%', value: 100 },
	]

	const handleModalOpen = () => {
		setIsIncentiveModalOpen(true)
	}

	const handleReturn = () => {
		setIsIncentiveModalOpen(false)
	}

	const handleFinishSubmit = (option, settingsData) => {
		setValue(`${activeRegion}.incentiveOption`, option)
		setValue(`${activeRegion}.incentiveSettings`, settingsData, { shouldValidate: true })
		setIsIncentiveModalOpen(false)
	}

	const toggleDropdown = () => {
		setIsDropdownOpen(!isDropdownOpen)
	}

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsDropdownOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	const formatNumberWithCommas = (value) => {
		if (value === null || value === undefined || value === '') return ''
		const numericValue = String(value).replace(/[^0-9]/g, '')
		if (numericValue === '') return ''
		return new Intl.NumberFormat('ko-KR').format(Number(numericValue))
	}

	const handlePriceInputChange = (event, rkHookFormOnChange) => {
		const inputValue = event.target.value
		const sanitizedValue = inputValue.replace(/[^0-9]/g, '')
		const formattedValue = formatNumberWithCommas(sanitizedValue)
		rkHookFormOnChange(formattedValue)
	}

	const onSubmit = async (data) => {
		try {
			console.log('선호 계약 정보 업데이트:', data)

			// 데이터 변환 (OnboardingNext와 동일한 로직)
			const transformedData = { ...data }

			// 국내 계약 데이터 변환
			if (transformedData.local) {
				// 컷 통과/탈락 금액 숫자 변환
				if (transformedData.local.cutPass) {
					transformedData.local.cutPass = Number(
						String(transformedData.local.cutPass).replace(/[^0-9]/g, ''),
					)
				}
				if (transformedData.local.cutOut) {
					transformedData.local.cutOut = Number(
						String(transformedData.local.cutOut).replace(/[^0-9]/g, ''),
					)
				}
				if (transformedData.local.jejudoMoney) {
					transformedData.local.jejudoMoney = Number(
						String(transformedData.local.jejudoMoney).replace(/[^0-9]/g, ''),
					)
				}
			}

			// 해외 계약 데이터 변환
			if (transformedData.global) {
				if (transformedData.global.cutPass) {
					transformedData.global.cutPass = Number(
						String(transformedData.global.cutPass).replace(/[^0-9]/g, ''),
					)
				}
				if (transformedData.global.cutOut) {
					transformedData.global.cutOut = Number(
						String(transformedData.global.cutOut).replace(/[^0-9]/g, ''),
					)
				}
			}

			// DTO에 맞는 데이터 구조로 변환
			const domesticContractData = {
				isSalaryTaxIncluded:
					transformedData.local.applyTax !== ''
						? transformedData.local.applyTax === 'after'
							? true
							: false
						: null,
				weeklySalaryCutThrough: transformedData.local.cutPass,
				weeklySalaryCutFail: transformedData.local.cutOut,
				payrollCycles:
					transformedData.local.payPeriod !== ''
						? transformedData.local.payPeriod === 'week'
							? 'weekly'
							: 'perGame'
						: null,
				jejuTournamentParticipation: transformedData.local.jejudo,
				expenseReimbursementJeju: transformedData.local.jejudo
					? Number(transformedData.local.jejudoMoney)
					: 0,
				wearSponsoredHat: transformedData.local.sponsorCap,
				prizeType:
					transformedData.local.incentiveOption === ''
						? null
						: transformedData.local.incentiveOption === 'amountReceived'
							? 'actual'
							: 'displayed',
				incentives: transformedData.local.incentiveSettings.map((option) => ({
					startRank: option.start,
					endRank: option.end === 'CUT' || option.end === 0 ? 0 : option.end,
					percentage: option.percentage,
				})),
			}

			const overseasContractData = {
				isSalaryTaxIncluded:
					transformedData.global.applyTax !== ''
						? transformedData.global.applyTax === 'after'
							? true
							: false
						: null,
				weeklySalaryCutThrough: transformedData.global.cutPass,
				weeklySalaryCutFail: transformedData.global.cutOut,
				payrollCycles:
					transformedData.global.payPeriod !== ''
						? transformedData.global.payPeriod === 'week'
							? 'weekly'
							: 'perGame'
						: null,
				expenseReimbursement: transformedData.global.overseasSupport,
				wearSponsoredHat: transformedData.global.sponsorCap,
				prizeType:
					transformedData.global.incentiveOption === ''
						? null
						: transformedData.global.incentiveOption === 'amountReceived'
							? 'actual'
							: 'displayed',
				incentives: transformedData.global.incentiveSettings.map((option) => ({
					startRank: option.start,
					endRank: option.end === 'CUT' || option.end === 0 ? 0 : option.end,
					percentage: option.percentage,
				})),
			}

			// 최종 API 전송 데이터 구조
			const dataReadyToSubmit =
				activeRegion === 'local'
					? { domesticContract: domesticContractData }
					: { overseasContract: overseasContractData }

			console.log('서버에 전송할 데이터:', dataReadyToSubmit)

			// API 호출
			const response = await updatePreferredContractInfo(token, dataReadyToSubmit)
			console.log('업데이트 성공:', response)
			await queryClient.invalidateQueries({ queryKey: ['preferred-contract'] })
			alert('선호 계약 정보가 업데이트되었습니다.')
		} catch (error) {
			console.error('선호 계약 정보 업데이트 실패:', error)

			if (error.response) {
				console.error('서버 응답 에러:', error.response.data, error.response.status)
				alert(
					`업데이트 실패: ${error.response.data.message || '알 수 없는 서버 오류입니다.'}`,
				)
			} else if (error.request) {
				console.error('네트워크 오류:', error.request)
				alert('네트워크 오류: 서버에 연결할 수 없습니다.')
			} else {
				console.error('요청 설정 중 오류:', error.message)
				alert('요청 중 문제가 발생했습니다.')
			}
		}
	}

	return (
		<div className={styles.contractWrapper}>
			<BackButton />
			<div className={styles.contractContent}>
				<div className={styles.contractTitleBox}>
					<h2 className={styles.contractTitle}>선호 계약 정보</h2>
					<span className={styles.explain}>
						계약정보를 저장해두고 언제든지 불러와서 사용하세요
					</span>
				</div>

				{/* 지역 토글 */}
				<div className={styles.regionToggle}>
					<button
						type="button"
						className={`${styles.regionButton} ${activeRegion === 'local' ? styles.active : ''}`}
						onClick={() => setActiveRegion('local')}>
						국내
					</button>
					<button
						type="button"
						className={`${styles.regionButton} ${activeRegion === 'global' ? styles.active : ''}`}
						onClick={() => setActiveRegion('global')}>
						해외
					</button>
				</div>

				{/* 국내 계약 정보 */}
				{activeRegion === 'local' && (
					<div className={styles.formSection}>
						<div className={styles.applyTax}>
							<ToggleButtonGroupController
								control={control}
								name="local.applyTax"
								toggleFieldLabel="임금 세금 포함 여부"
								isNoticeNeed={true}
								isNoticeNeedText={`세전 : 3.3% 제외 후 입금 예정\n세후 : 3.3% 제외된 실 입금 금액`}
								toggleOption={applyTaxOption}
								rules={{ required: '임금 세금포함여부 골라주세요' }}
							/>
						</div>
						<div className={styles.payPeriod}>
							<ToggleButtonGroupController
								control={control}
								name="local.payPeriod"
								toggleFieldLabel="임금 지급 주기"
								toggleOption={payPeriodOption}
								rules={{ required: '임금 지급 주기 골라주세요' }}
							/>
						</div>
						<div className={styles.cutPass}>
							<SimpleInputController
								control={control}
								name="local.cutPass"
								rules={{ required: '컷 통과 금액을 입력해주세요' }}
								inputLabel="컷통과"
								inputPlaceholder="컷 통과시 지급할 금액을 입력해주세요"
								onChange={handlePriceInputChange}
							/>
						</div>
						<div className={styles.cutOut}>
							<SimpleInputController
								control={control}
								name="local.cutOut"
								rules={{ required: '컷 탈락 금액을 입력해주세요' }}
								inputLabel="컷탈락"
								inputPlaceholder="컷 탈락시 지급할 금액을 입력해주세요"
								onChange={handlePriceInputChange}
							/>
						</div>
						<div className={styles.incentive}>
							<div className={styles.incentiveInfo}>
								인센티브
								<InformationCircleIcon
									className={styles.noticeIcon}
									data-tooltip-id="my-tooltip-local"
									data-tooltip-content={`상금 표기액 : 각 투어별 홈페이지에 명시된 금액 \n 상금 실수령액 : KPGA 10%, KLPGA 9.3% 제외된 금액`}
								/>
								<Tooltip
									id="my-tooltip-local"
									place="top"
									className={styles.tooltip}
								/>
							</div>
							<button type="button" onClick={handleModalOpen}>
								입력하기
							</button>
						</div>
						<Controller
							name="local.incentiveSettings"
							control={control}
							rules={{
								validate: (value) =>
									value && value.length > 0
										? true
										: '인센티브 설정을 입력해주세요.',
							}}
							render={({ field }) => <input type="hidden" {...field} />}
						/>
						{activeData?.incentiveSettings &&
							activeData.incentiveSettings.length > 0 && (
								<div className={styles.incentiveDisplaySection}>
									{activeData.incentiveSettings.map((setting, index) => {
										if (setting.percentage === null && setting.end === null)
											return null
										const endLabel =
											setting.end === 'CUT' || setting.end === 0
												? 'CUT'
												: setting.end
										const displayRank =
											setting.id === 1
												? '우승 '
												: setting.start +
													(setting.end != null ? ` ~ ${endLabel} ` : '~ ')
										const displayPercentage =
											setting.percentage !== null
												? `${setting.percentage}%`
												: '미입력'
										return (
											<div
												key={setting.id}
												className={styles.incentiveDisplayItem}>
												<span>{displayRank}</span>
												<span>: {displayPercentage}</span>
												{index !==
													activeData.incentiveSettings.length - 1 && (
													<span> / </span>
												)}
											</div>
										)
									})}
								</div>
							)}

						<div className={styles.jejudo}>
							<ToggleButtonGroupController
								control={control}
								name="local.jejudo"
								isNoticeNeed={true}
								isNoticeNeedText={`제주대회 출전 시 : 세후 금액으로 3.3%으로 제외된 금액`}
								toggleFieldLabel="제주대회 출전 시"
								toggleOption={jejudoOption}
							/>
							<div className={`${jejudo ? styles.jejudoInput : styles.none}`}>
								<SimpleInputController
									control={control}
									name="local.jejudoMoney"
									isLabel={false}
									inputPlaceholder="제주대회 지원금액을 입력해주세요"
									onChange={handlePriceInputChange}
									rules={{
										validate: (value) => {
											const currentJejudo = watch('local.jejudo')
											if (currentJejudo === true) {
												return value && value.trim() !== ''
													? true
													: '제주대회 지원금액을 입력해주세요.'
											}
											return true
										},
									}}
								/>
							</div>
							<div className={styles.sponsorCap}>
								<ToggleButtonGroupController
									control={control}
									name="local.sponsorCap"
									toggleFieldLabel="선수 스폰서 모자 착용"
									toggleOption={sponsorCapOption}
								/>
							</div>
						</div>
					</div>
				)}

				{/* 해외 계약 정보 */}
				{activeRegion === 'global' && (
					<div className={styles.formSection}>
						<div className={styles.applyTax}>
							<ToggleButtonGroupController
								control={control}
								name="global.applyTax"
								isNoticeNeed={true}
								isNoticeNeedText={`세전 : 3.3% 제외 후 입금 예정\n세후 : 3.3% 제외된 실 입금 금액`}
								toggleFieldLabel="임금 세금 포함 여부"
								toggleOption={applyTaxOption}
								rules={{ required: '임금 세금포함여부를 선택해주세요' }}
							/>
						</div>
						<div className={styles.payPeriod}>
							<ToggleButtonGroupController
								control={control}
								name="global.payPeriod"
								toggleFieldLabel="임금 지급 주기"
								toggleOption={payPeriodOption}
								rules={{ required: '임금 지급 주기를 선택해주세요' }}
							/>
						</div>
						<div className={styles.cutPass}>
							<SimpleInputController
								control={control}
								name="global.cutPass"
								rules={{ required: '컷 통과 금액을 입력해주세요' }}
								inputLabel="컷통과"
								inputPlaceholder="컷 통과시 지급할 금액을 입력해주세요"
								onChange={handlePriceInputChange}
							/>
						</div>
						<div className={styles.cutOut}>
							<SimpleInputController
								control={control}
								name="global.cutOut"
								rules={{ required: '컷 탈락 금액을 입력해주세요' }}
								inputLabel="컷탈락"
								inputPlaceholder="컷 탈락시 지급할 금액을 입력해주세요"
								onChange={handlePriceInputChange}
							/>
						</div>
						<div className={styles.incentive}>
							<div className={styles.incentiveInfo}>
								인센티브
								<InformationCircleIcon
									className={styles.noticeIcon}
									data-tooltip-id="my-tooltip-global"
									data-tooltip-content={`상금 표기액 : 각 투어별 홈페이지에 명시된 금액 \n 상금 실수령액 : KPGA 10%, KLPGA 9.3% 제외된 금액`}
								/>
								<Tooltip
									id="my-tooltip-global"
									place="top"
									className={styles.tooltip}
								/>
							</div>
							<button type="button" onClick={handleModalOpen}>
								입력하기
							</button>
						</div>
						<Controller
							name="global.incentiveSettings"
							control={control}
							rules={{
								validate: (value) =>
									value && value.length > 0
										? true
										: '인센티브 설정을 입력해주세요.',
							}}
							render={({ field }) => <input type="hidden" {...field} />}
						/>
						{activeData?.incentiveSettings &&
							activeData.incentiveSettings.length > 0 && (
								<div className={styles.incentiveDisplaySection}>
									{activeData.incentiveSettings.map((setting) => {
										if (setting.percentage === null && setting.end === null)
											return null
										const endLabel =
											setting.end === 'CUT' || setting.end === 0
												? 'CUT'
												: setting.end
										const displayRank =
											setting.id === 1
												? '우승 '
												: setting.start +
													(setting.end != null ? ` ~ ${endLabel} ` : '~ ')
										const displayPercentage =
											setting.percentage !== null
												? `${setting.percentage}%`
												: '미입력'
										return (
											<div
												key={setting.id}
												className={styles.incentiveDisplayItem}>
												<span>{displayRank}</span>
												<span>: {displayPercentage}</span>
											</div>
										)
									})}
								</div>
							)}
						<div className={styles.overSeasSupport}>
							<Controller
								name="global.overseasSupport"
								control={control}
								rules={{ required: '해외대회 지원 항목을 선택해주세요' }}
								render={({ field }) => (
									<>
										<div className={styles.overSeasSupportTitle}>
											해외대회 지원 항목
										</div>
										<div
											className={styles.overSeasSupportContainer}
											ref={dropdownRef}>
											<div
												className={styles.overSeasSupportDropDownInput}
												onClick={toggleDropdown}
												role="button"
												aria-haspopup="listbox"
												aria-expanded={isDropdownOpen}>
												{field.value === undefined ||
												field.value === null ? (
													<span
														className={
															styles.overSeasSupportDropDownPlaceholder
														}>
														해외대회 지원 항목을 선택해주세요
													</span>
												) : (
													<span className={styles.overSeasSupportDisplay}>
														{field.value === 0
															? '미지원'
															: field.value + '%'}
													</span>
												)}
												<ChevronDownIcon className={styles.dropDownArrow} />
											</div>
											{isDropdownOpen && (
												<ul
													className={`${styles.dropDownList} ${isDropdownOpen ? styles.dropDownListOpened : styles.dropDownListClosed}`}>
													{overseasSupportOptions.map((option) => (
														<li
															key={option.value}
															className={styles.dropDownListItem}
															onClick={(e) => {
																e.stopPropagation()
																field.onChange(option.value)
																setIsDropdownOpen(false)
															}}>
															{option.label}
														</li>
													))}
												</ul>
											)}
										</div>
									</>
								)}
							/>
						</div>
						<div className={styles.sponsorCap}>
							<ToggleButtonGroupController
								control={control}
								name="global.sponsorCap"
								toggleFieldLabel="선수 스폰서 모자 착용"
								toggleOption={sponsorCapOption}
							/>
						</div>
					</div>
				)}

				<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
					<button
						className={styles.nextButton}
						onClick={handleSubmit(onSubmit)}
						disabled={
							(activeRegion === 'local' && !isLocalValid) ||
							(activeRegion === 'global' && !isGlobalValid)
						}>
						저장
					</button>
				</div>
			</div>

			<IncentiveModal
				isModalOpen={isIncentiveModalOpen}
				onFinish={handleFinishSubmit}
				onReturn={handleReturn}
				initialSettings={activeData?.incentiveSettings}
			/>
		</div>
	)
}
